/**
 * Real ADK session history — replaces the old mia-flow-be-backed Conversations
 * service (Conversations.js's searchConversations, a CRUD query against
 * mia-flow-be's own `/conversations` collection) for the Memories page.
 *
 * Every vfs-backed chat already resumes via VfsChatPanel's deterministic
 * initialSessionId (see that file), and mia-chatbot-widget already knows how
 * to list/rename/pin/archive/delete these same sessions inside its own
 * ChatHistoryModal (src/hooks/useChat.ts's listSessions/history). This mirrors
 * that same adk-be-app endpoint (adkClient.ts's listSessions) directly, since
 * Memories is a standalone page outside <ChatWidget> and needs its own call.
 *
 * listAdkSessions below used to hardcode ONE fixed app_name
 * (code_generation_playbook_code_generation_agent) — meaning ANY session
 * created under a different playbook/agent (a second coding playbook, a
 * documentation assistant, anything not code-generation) was silently
 * invisible on this page, even though it belonged to the same user.
 * Live-confirmed: a brand-new second coding playbook's sessions never
 * appeared here. Now calls adk-be-app's GET /api/sessions-all/{userId},
 * which merges sessions across every app_name the user actually has one
 * under (see that endpoint's own docstring, src/routes/session_labels.py).
 * `sessionPath` below still needs A specific app_name for its per-session
 * label/rename calls — those are session-scoped, not listing-scoped, and
 * each session's own `appName` field (returned by listAdkSessions) is used
 * for that instead of this constant now.
 */

import config from '../config'
import { getAuthorizedFetch } from '../utils/fetchRegistry'
import { buildAclContextHeaders } from '../utils/aclContext'
import { shareInflight } from '../utils/sharedRequest'

const parseJson = async (response) => {
  if (!response.ok) {
    const error = new Error(`Request failed with status ${response.status}`)
    error.status = response.status
    throw error
  }
  return response.json()
}

const sessionPath = (appName, userId, sessionId) =>
  `/apps/${encodeURIComponent(appName)}/users/${encodeURIComponent(userId)}/sessions/${encodeURIComponent(sessionId)}`

/**
 * GET /api/sessions-all/{userId} — every real ADK session for this user,
 * across every playbook/agent (app_name) they've ever used, not just one.
 * @param {string} userId
 * @returns {Promise<Array<{id: string, appName: string, userId: string, name: string|null, lastUpdateTime: number, state?: object}>>}
 */
export const listAdkSessions = async (userId) => {
  const base = (config.vfsAgentUrl || '').replace(/\/+$/, '')
  const url = `${base}/api/sessions-all/${encodeURIComponent(userId)}`
  const response = await getAuthorizedFetch()(url, {
    headers: { Accept: 'application/json', ...buildAclContextHeaders() },
  })
  const data = await parseJson(response)
  return Array.isArray(data) ? data : (data.items ?? [])
}

/** PUT {sessionPath}/label — the same rename mechanism the widget's own
 *  ChatHistoryModal uses (adkClient.ts's renameSession). */
export const renameAdkSession = async (appName, userId, sessionId, name) => {
  const base = (config.vfsAgentUrl || '').replace(/\/+$/, '')
  const response = await getAuthorizedFetch()(`${base}${sessionPath(appName, userId, sessionId)}/label`, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'content-type': 'application/json', ...buildAclContextHeaders() },
    body: JSON.stringify({ name }),
  })
  if (!response.ok) {
    const error = new Error(`Request failed with status ${response.status}`)
    error.status = response.status
    throw error
  }
}

/**
 * POST {sessionPath}/label/generate — (re)triggers adk-be-app's own
 * fire-and-forget session-naming pipeline (src/agent/session_namer.py) for
 * one session, synchronously this time. Used to backfill sessions that
 * show as "Untitled Conversation": the automatic naming on the live chat
 * only retries 3 times over ~24s after the first turn, then gives up
 * forever, even on a later revisit (see mia-chatbot-widget's
 * hydrateSession, `labelFetchDoneRef`) — a session whose first attempt
 * missed that window (LLM latency, a transient error) never gets a second
 * chance without calling this explicitly. Returns the label name (whether
 * freshly generated or already existing — this never overwrites a real
 * label since `force` is never passed).
 */
export const generateAdkSessionLabel = async (appName, userId, sessionId) => {
  const base = (config.vfsAgentUrl || '').replace(/\/+$/, '')
  const response = await getAuthorizedFetch()(`${base}${sessionPath(appName, userId, sessionId)}/label/generate`, {
    method: 'POST',
    headers: { Accept: 'application/json', ...buildAclContextHeaders() },
  })
  const data = await parseJson(response)
  return data.name
}

/**
 * Pin/archive via the session's own generic state, NOT a dedicated
 * `/pin`/`/archive` route: this adk-be-app build's real OpenAPI surface
 * (confirmed live, GET /openapi.json) has no such routes — the vendored
 * mia-chatbot-widget's `setSessionPinned`/`setSessionArchived` (adkClient.ts)
 * assume a backend feature this deployment doesn't implement, so calling
 * those paths would just 404. What DOES exist and IS real: `PATCH
 * {sessionPath}` taking `{stateDelta: {...}}`, which merges arbitrary keys
 * into the session's `state` object (confirmed live: every session already
 * carries a real `state` object, e.g. `{"permission_mode": "auto"}`). Store
 * `pinned`/`archived` as plain keys there — `listAdkSessions`'s response
 * already includes each session's full `state`, so Memories can read
 * `session.state?.pinned`/`.archived` back with no extra round-trip.
 */
const patchSessionState = async (appName, userId, sessionId, stateDelta) => {
  const base = (config.vfsAgentUrl || '').replace(/\/+$/, '')
  const response = await getAuthorizedFetch()(`${base}${sessionPath(appName, userId, sessionId)}`, {
    method: 'PATCH',
    headers: { Accept: 'application/json', 'content-type': 'application/json', ...buildAclContextHeaders() },
    body: JSON.stringify({ stateDelta }),
  })
  return parseJson(response)
}

export const setSessionPinned = (appName, userId, sessionId, pinned) =>
  patchSessionState(appName, userId, sessionId, { pinned })

export const setSessionArchived = (appName, userId, sessionId, archived) =>
  patchSessionState(appName, userId, sessionId, { archived })

/**
 * POST /api/sessions/{appName}/{userId}/{sessionId}/cancel — adk-be-app's
 * `src.routes.run_control.cancel_run`: flags the session's active `/run`/
 * `/run_sse` invocation for graceful cancellation at its next safe
 * checkpoint (finishes whatever tool call is in flight, then stops before
 * the next model/tool step) rather than severing the connection outright.
 * Used by the History/Kanban board's "drag out of Active" / "Stop" actions.
 * A 404 ("no active run for this session") is treated as an already-quiet
 * success, not an error — the run may simply have finished on its own in
 * the time between this session showing as Active and the user acting on
 * it, and the caller's actual intent ("I don't want this running") is
 * already satisfied either way.
 */
export const cancelAdkSession = async (appName, userId, sessionId) => {
  const base = (config.vfsAgentUrl || '').replace(/\/+$/, '')
  const response = await getAuthorizedFetch()(`${base}/api/sessions/${encodeURIComponent(appName)}/${encodeURIComponent(userId)}/${encodeURIComponent(sessionId)}/cancel`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'content-type': 'application/json', ...buildAclContextHeaders() },
  })
  if (!response.ok && response.status !== 404) {
    const error = new Error(`Request failed with status ${response.status}`)
    error.status = response.status
    throw error
  }
}

/**
 * GET {sessionPath} — the real ADK session, WITH its full persisted event
 * log (`events: [...]`) — unlike `listAdkSessions`/`sessions-all`, whose
 * underlying `list_sessions` call is documented (adk-be-app's own comment
 * on `_list_sessions_for_app`) to not reliably populate `events`/`state`
 * for a listing. Used by the History/Kanban step drawer to reconstruct a
 * session's tool-call steps — see `utils/sessionSteps.js`.
 */
export const getAdkSession = async (appName, userId, sessionId) => {
  const base = (config.vfsAgentUrl || '').replace(/\/+$/, '')
  const response = await getAuthorizedFetch()(`${base}${sessionPath(appName, userId, sessionId)}`, {
    headers: { Accept: 'application/json', ...buildAclContextHeaders() },
  })
  return parseJson(response)
}

/**
 * GET {sessionPath}/children — this session's `parallel`/`fork` child
 * sessions (adk-be-app's src/routes/session_labels.py::get_session_children),
 * each with a live/finished status. A session with any children is, in this
 * app's vocabulary, "a workflow" — see the History/Kanban step drawer.
 * @returns {Promise<Array<{id: string, appName: string, relation: string, name: string|null, lastUpdateTime: number, isActive: boolean}>>}
 */
// An adk-be-app without the children route answers 404 for every session: once seen, Flow stops asking (no
// workflow children to show), instead of one failing request per opened session.
let childrenRouteMissing = false

/** Tests only. */
export const resetSessionChildrenSupport = () => { childrenRouteMissing = false }

export const getSessionChildren = async (appName, userId, sessionId) => {
  if (childrenRouteMissing) return []
  const base = (config.vfsAgentUrl || '').replace(/\/+$/, '')
  const response = await getAuthorizedFetch()(`${base}${sessionPath(appName, userId, sessionId)}/children`, {
    headers: { Accept: 'application/json', ...buildAclContextHeaders() },
  })
  if (response.status === 404) {
    childrenRouteMissing = true
    return []
  }
  const data = await parseJson(response)
  return Array.isArray(data.children) ? data.children : []
}

/** DELETE {sessionPath} — deletes the real ADK session, not just its label. */
export const deleteAdkSession = async (appName, userId, sessionId) => {
  const base = (config.vfsAgentUrl || '').replace(/\/+$/, '')
  const response = await getAuthorizedFetch()(`${base}${sessionPath(appName, userId, sessionId)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...buildAclContextHeaders() },
  })
  if (!response.ok) {
    const error = new Error(`Request failed with status ${response.status}`)
    error.status = response.status
    throw error
  }
}

/**
 * VfsChatPanel's session ids are deterministic: `vfs-{projectId}` or
 * `vfs-{projectId}-{serviceName}` (see that file). Parsed back out here so
 * Memories can show which project a session belongs to and build the right
 * /code/... link — MongoDB ObjectIds never contain a dash, so splitting on
 * the first one after the `vfs-` prefix is unambiguous.
 */
export const parseVfsSessionId = (sessionId) => {
  const match = /^vfs-([^-]+)(?:-(.+))?$/.exec(sessionId || '')
  if (!match) { return null }
  return { projectId: match[1], serviceName: match[2] || null }
}

/**
 * GET /api/sessions/{appName}/{userId}/active — "background tasks": the
 * subset of this user's sessions that currently have an in-flight
 * `/run`/`/run_sse` request (adk-be-app's `active_runs` in-memory registry).
 * Meant for lightweight polling (e.g. every few seconds) to drive a live
 * "N tasks running" indicator — not for anything that needs durability or
 * cross-replica correctness.
 * @param {string} userId
 * @returns {Promise<string[]>} active session ids
 */
export const listActiveAdkSessions = (userId) => shareInflight(`adk-active:${userId}`, async () => {
  const base = (config.vfsAgentUrl || '').replace(/\/+$/, '')
  // The {app_name} segment is accepted for URL-shape compatibility only —
  // the backend explicitly ignores it and checks across all of the user's
  // apps (see this route's own docstring in adk-be-app), so any literal
  // works here.
  const url = `${base}/api/sessions/_/${encodeURIComponent(userId)}/active`
  const response = await getAuthorizedFetch()(url, {
    headers: { Accept: 'application/json', ...buildAclContextHeaders() },
  })
  const data = await parseJson(response)
  return Array.isArray(data.activeSessionIds) ? data.activeSessionIds : []
})
